import { AxiosPromise } from 'axios';
import { api } from 'src/services';

/**
 * Create appointment (EHR)
 *
 * Backend:
 * - POST /v3/ehr/appointment
 */
export type CreateEhrAppointmentApiPayload = {
  department_id: string | number;
  provider_id: string | number;
  appointment_type_id: string | number;
  appointment_id: string | number;
};

export type EhrAppointment = {
  appointment_id: string;
  appointment_type_id: string;
  appointment_date: string;
  appointment_time: string;
  appointment_location: string;
  appointment_provider: string;
};
export type CreateEhrAppointmentApiResponse = EhrAppointment;

export const createEhrAppointmentApi = (
  payload: CreateEhrAppointmentApiPayload
): AxiosPromise<CreateEhrAppointmentApiResponse> => {
  return api.post<CreateEhrAppointmentApiResponse>(
    '/v3/ehr/appointment',
    payload
  );
};

/**
 * Get appointment slots (EHR)
 *
 * Backend:
 * - GET /v3/ehr/appointment/slots?department_id=&provider_id=&start_date=&end_date=
 */
export type GetEhrAppointmentSlotsApiPayload = {
  department_id?: string | number;
  provider_id?: string | number;
  start_date?: string;
  end_date?: string;
};

export type EhrAppointmentSlot = {
  appointment_id: number;
  appointment_type: string;
  appointment_type_id: number;
  available: boolean;
  date: string;
  datetime: string;
  department_id: number;
  duration: number;
  end_time: string;
  frozen: boolean;
  local_provider_id: number;
  patient_appointment_type_name: string;
  provider_id: number;
  reason_id: number | null;
  rendering_provider_id: number | null;
  time: string;
  valid_appointment_type_ids: number[] | null;
};

export type GetEhrAppointmentSlotsApiResponse = EhrAppointmentSlot[];

export const getEhrAppointmentSlotsApi = ({
  department_id,
  provider_id,
  start_date,
  end_date,
}: GetEhrAppointmentSlotsApiPayload): AxiosPromise<GetEhrAppointmentSlotsApiResponse> => {
  const params = {
    department_id,
    provider_id,
    start_date,
    end_date,
  };

  return api.get<GetEhrAppointmentSlotsApiResponse>(
    '/v3/ehr/appointment/slots',
    {
      params,
    }
  );
};

/**
 * Get providers (EHR)
 *
 * Backend:
 * - GET /v3/ehr/providers
 */
export type EhrProvider = {
  billable: boolean;
  degree: string;
  first_name: string;
  home_department: string;
  id: number;
  last_name: string;
  name: string;
  provider_type_id: string;
  scheduling_name: string;
  specialization: string;
};

export type GetEhrProvidersApiResponse = EhrProvider[];

export const getEhrProvidersApi =
  (): AxiosPromise<GetEhrProvidersApiResponse> => {
    return api.get<GetEhrProvidersApiResponse>('/v3/ehr/providers');
  };

/**
 * Get departments (EHR)
 *
 * Backend:
 * - GET /v3/ehr/departments
 */
export type EhrDepartment = {
  address: string;
  address2: string | null;
  city: string;
  clinicals: string;
  department_id: string;
  does_not_observe_dst: boolean;
  fax: string | null;
  is_hospital_department: boolean;
  latitude: string;
  longitude: string;
  medication_history_consent: boolean;
  name: string;
  patient_department_name: string;
  phone: string | null;
  place_of_service_type_name: string;
  portal_url: string | null;
  provider_group_name: string;
  service_department: boolean;
  state: string;
  time_zone_name: string;
  time_zone_offset: number;
  zip: string;
};

export type GetEhrDepartmentsApiResponse = EhrDepartment[];

export const getEhrDepartmentsApi =
  (): AxiosPromise<GetEhrDepartmentsApiResponse> => {
    return api.get<GetEhrDepartmentsApiResponse>('/v3/ehr/departments');
  };
